import _ from 'lodash';

/**
 * Takes an array of keys and returns a function that can be used to filter an array of objects.
 *
 * NOTE: the `keys` can be nested (e.g. "this.that")
 *
 * The returned function will take a `filterValue` and return a function that
 * returns `true` if any of the values at `keys` in the object provided to it
 * contain case insensitive matches for each part of the `filterValue`, which is split by spaces.
 * If the value at key is an array, it will return true if any of the values in the array match.
 *
 * For example:
 * with keys = ['firstName', 'lastName'], filterValue = 'bob smith'
 * if obj = {firstName: 'Bob', lastName: 'Smith'}, it would return true because both "bob" and "smith" match a value
 * if obj = {firstName: 'Joe', lastName: 'Smith Bob'}, it would also be true
 * but if obj = {firstName: 'Bob', lastName: 'Jones'}, it would be false since "smith" has no match
 *
 * @param {string[]} keys
 *
 * @returns {(filterValue: string) => (obj: Record<string, any>) => boolean}
 */
export const searchBy = (keys) => (filterValue) => (obj) => {
  const filters = filterValue.split(' ').map((f) => f.toLowerCase());
  // All of the words passed in must match either the project or team name
  return filters.every((filter) =>
    keys.some((key) => {
      const value = _.get(obj, key);
      // if the value is an array, check if any of the values match
      if (Array.isArray(value)) {
        return value.some((v) => v?.toLowerCase()?.includes(filter));
      }
      return value?.toLowerCase()?.includes(filter);
    }),
  );
};

/**
 * takes a single string filterValue, and an optional max n-gram length
 * and returns an array of all possible ordered n-grams from the filterValue
 * for example, if filterValue is "hello world" the output would be:
 * ["hello", "hello world", "world"]
 * but would not include "world hello"
 */
export const getNGrams = (filterValue, n = 3) => {
  const filters = filterValue
    .split(' ')
    .map((f) => f.toLowerCase())
    .filter((f) => f.length > 0);
  const nonUniqueNGrams = _.flatMap(filters, (_unused, i) => {
    const toReturn = [];
    for (let j = i; j < Math.min(i + n, filters.length); j += 1) {
      toReturn.push(filters.slice(i, j + 1).join(' '));
    }
    return toReturn;
  });
  return _.uniq(nonUniqueNGrams);
};

/**
 * takes an array of ngrams and a string to match
 * and returns the ngram from the list which is the longest and first alphabetically
 * to match a sequence in stringToMatch
 */
export const getBestMatchForHighlight = (nGrams) => (stringToMatch) => {
  // sort by length of ngram, then by the ngram itself
  const matches = nGrams.filter((ngram) =>
    stringToMatch.toLowerCase().includes(ngram),
  );
  if (matches.length === 0) {
    return '';
  }
  // the first entry should be the longest word
  // if there are ties on length, it should be the first alphabetically
  return _.orderBy(
    matches,
    [(ngram) => ngram.length, (ngram) => ngram],
    ['desc', 'asc'],
  )[0];
};
